import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import DurationDisplay from '../common/DurationDisplay';

const CAMPAIGN_OPTIONS = [];

const LEVEL_OPTIONS = [
  { value: 'all', label: 'Todos los niveles' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Advertencia' },
  { value: 'error', label: 'Error' },
  { value: 'critical', label: 'Crítico' },
];

export default function AdBroadcasts() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchCampaigns();
    fetchBroadcasts();
  }, [levelFilter, campaignFilter, dateFrom, dateTo]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get('/api/campaigns');
      setCampaigns(data);
    } catch (err) {
      console.error('[AdBroadcasts] Failed to fetch campaigns:', err);
    }
  };

  const fetchBroadcasts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (levelFilter !== 'all') params.level = levelFilter;
      if (campaignFilter) params.campaign_id = campaignFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const { data } = await api.get('/api/advertising/broadcasts', { params });
      setBroadcasts(data);
    } catch (err) {
      console.error('[AdBroadcasts] Failed to fetch broadcasts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setLevelFilter('all');
    setCampaignFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = levelFilter !== 'all' || campaignFilter || dateFrom || dateTo;

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando emisiones de publicidad...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Emisiones de Publicidad</h1>
        <div className="flex gap-2">
          <button
            onClick={fetchBroadcasts}
            className="btn btn-secondary btn-sm"
            title="Actualizar"
          >
            ↻
          </button>
          {hasActiveFilters && (
            <button onClick={handleClearFilters} className="btn btn-secondary btn-sm text-xs">
              Limpiar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          className="input text-sm"
        >
          {LEVEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={campaignFilter}
          onChange={(e) => setCampaignFilter(e.target.value)}
          className="input text-sm"
        >
          <option value="">Todas las campañas</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="input text-sm"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="input text-sm"
        />
      </div>

      {broadcasts.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No se encontraron emisiones. {hasActiveFilters && 'Intenta limpiar los filtros.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Fecha/Hora</th>
                <th>Spot</th>
                <th>Campaña</th>
                <th>Anunciante</th>
                <th>Duración</th>
                <th>Reproducciones</th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((b) => (
                <tr key={b.id}>
                  <td className="text-sm text-dark-300">
                    {b.broadcast_at ? format(parseISO(b.broadcast_at), 'PPpp') : '—'}
                  </td>
                  <td className="font-medium text-dark-100">{b.spot_title || '—'}</td>
                  <td className="text-dark-300">{b.campaign_name || '—'}</td>
                  <td className="text-dark-300">{b.advertiser_name || '—'}</td>
                  <td className="text-sm text-dark-400">
                    {b.duration_seconds && <DurationDisplay seconds={b.duration_seconds} />}
                  </td>
                  <td className="text-dark-300">{b.play_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
